import React, { useCallback, useEffect, useRef, useState } from "react";
import { ArrowRight, Monitor, Radio, RefreshCw, Smartphone, Tablet } from "lucide-react";
import { API, Modal, Pagination, SearchBar, Skeleton, apiFetch, cx, iCls } from "../../../pages/admin/_shared";
import { ChartCard, fmtNum } from "../charts";

// ─── Tên dễ đọc cho đường dẫn ────────────────────────────────────────────────
const STATIC_PAGES = {
  "/": "Trang chủ",
  "/map": "Bản đồ",
  "/locations": "Tất cả địa điểm",
  "/about": "Giới thiệu",
  "/favorites": "Yêu thích",
  "/login": "Đăng nhập",
  "/register": "Đăng ký",
  "/profile": "Trang cá nhân",
};
const prettySlug = (slug) => {
  const s = decodeURIComponent(String(slug || "")).replace(/[-_]+/g, " ").trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : "";
};

/** "/locations?category=am-thuc-local" → "Danh mục: Am thuc local"; "/location/dinh-3" → tên địa điểm nếu biết. */
export function pathLabel(path, nameBySlug = new Map()) {
  const raw = String(path || "/");
  const [pathname, query = ""] = raw.split("?");
  if (pathname.startsWith("/location/")) {
    const slug = pathname.slice("/location/".length).split("/")[0];
    return nameBySlug.get(slug) || prettySlug(slug) || raw;
  }
  if (pathname === "/locations" && query) {
    const cat = new URLSearchParams(query).get("category");
    if (cat) return `Danh mục: ${prettySlug(cat)}`;
    const q = new URLSearchParams(query).get("q") || new URLSearchParams(query).get("search");
    if (q) return `Tìm kiếm: “${q}”`;
  }
  return STATIC_PAGES[pathname] || raw;
}

// ─── Giờ cao điểm: lưới 7 ngày × 24 giờ ─────────────────────────────────────
const DOW = ["T2", "T3", "T4", "T5", "T6", "T7", "CN"];
const DOW_FULL = ["Thứ Hai", "Thứ Ba", "Thứ Tư", "Thứ Năm", "Thứ Sáu", "Thứ Bảy", "Chủ nhật"];

export function HourHeatmap({ heat }) {
  const grid = Array.isArray(heat) && heat.length === 7 ? heat : Array.from({ length: 7 }, () => Array(24).fill(0));
  const max = Math.max(1, ...grid.flat());
  const total = grid.flat().reduce((a, b) => a + b, 0);
  if (!total) return <p className="py-6 text-center text-sm text-admin-textMuted">Chưa có dữ liệu</p>;

  // Giờ và ngày đông nhất
  let peak = { d: 0, h: 0, n: -1 };
  grid.forEach((row, d) => row.forEach((n, h) => n > peak.n && (peak = { d, h, n })));
  const byHour = Array.from({ length: 24 }, (_, h) => grid.reduce((a, row) => a + row[h], 0));
  const byDay = grid.map((row) => row.reduce((a, b) => a + b, 0));
  const peakHour = byHour.indexOf(Math.max(...byHour));
  const peakDay = byDay.indexOf(Math.max(...byDay));

  return (
    <div>
      <div className="mb-4 flex flex-wrap gap-2 text-xs">
        <span className="rounded-full bg-admin-primaryLight px-2.5 py-1 font-medium text-admin-primary">
          Đông nhất: {DOW_FULL[peak.d]} lúc {peak.h}h ({fmtNum(peak.n)} phiên)
        </span>
        <span className="rounded-full bg-admin-muted px-2.5 py-1 text-admin-text">Khung giờ đông: {peakHour}h–{peakHour + 1}h</span>
        <span className="rounded-full bg-admin-muted px-2.5 py-1 text-admin-text">Ngày đông: {DOW_FULL[peakDay]}</span>
      </div>
      <div className="overflow-x-auto">
        <div className="min-w-[560px]">
          <div className="grid gap-[3px]" style={{ gridTemplateColumns: "28px repeat(24, minmax(0, 1fr))" }}>
            {grid.map((row, d) => (
              <React.Fragment key={d}>
                <span className="flex items-center text-[10px] font-medium text-admin-textMuted">{DOW[d]}</span>
                {row.map((n, h) => (
                  <span
                    key={h}
                    title={`${DOW_FULL[d]}, ${h}h–${h + 1}h: ${fmtNum(n)} phiên`}
                    className="aspect-square rounded-[3px]"
                    style={{ background: n ? `rgb(var(--admin-primary) / ${0.12 + 0.88 * (n / max)})` : "rgb(var(--admin-muted))" }}
                  />
                ))}
              </React.Fragment>
            ))}
            <span />
            {Array.from({ length: 24 }, (_, h) => (
              <span key={h} className="text-center text-[9px] tabular-nums text-admin-textMuted">
                {h % 3 === 0 ? h : ""}
              </span>
            ))}
          </div>
          <div className="mt-3 flex items-center justify-end gap-1.5 text-[10px] text-admin-textMuted">
            Ít
            {[0.12, 0.35, 0.6, 0.85, 1].map((o) => (
              <span key={o} className="h-2.5 w-2.5 rounded-[2px]" style={{ background: `rgb(var(--admin-primary) / ${o})` }} />
            ))}
            Nhiều
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Phiên truy cập gần nhất (tự làm mới mỗi 30 giây) ───────────────────────────────────
const DEVICE_ICON = { mobile: Smartphone, tablet: Tablet, desktop: Monitor };
const fmtDur = (s) => {
  const n = Math.round(Number(s) || 0);
  if (n <= 0) return "—";
  if (n < 60) return `${n}s`;
  return `${Math.floor(n / 60)}p${n % 60 ? ` ${n % 60}s` : ""}`;
};
const ago = (iso) => {
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return "vừa xong";
  if (s < 3600) return `${Math.floor(s / 60)} phút trước`;
  if (s < 86400) return `${Math.floor(s / 3600)} giờ trước`;
  return new Date(iso).toLocaleDateString("vi-VN");
};

export function LivePanel({ nameBySlug }) {
  const [live, setLive] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setBusy(true);
    try {
      setLive(await apiFetch(`${API}/analytics/admin/live`));
    } catch {
      /* giữ số cũ */
    } finally {
      setBusy(false);
    }
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(() => document.visibilityState === "visible" && load(), 30000);
    return () => clearInterval(t);
  }, [load]);

  const [allOpen, setAllOpen] = useState(false);
  const recent = (live?.recent || []).slice(0, 8);

  return (
    <ChartCard
      icon={Radio}
      tone="emerald"
      title="Phiên truy cập gần nhất"
      subtitle="Tự cập nhật mỗi 30 giây · đã loại bot"
      action={
        <button type="button" onClick={load} title="Làm mới" aria-label="Làm mới" className="flex h-8 w-8 items-center justify-center rounded-lg text-admin-textMuted transition hover:bg-admin-muted hover:text-admin-text">
          <RefreshCw size={15} className={busy ? "animate-spin" : ""} />
        </button>
      }
    >
      <SessionTable rows={recent} nameBySlug={nameBySlug} />
      <button
        type="button"
        onClick={() => setAllOpen(true)}
        className="mt-4 inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-admin-border bg-admin-surface py-2 text-sm font-semibold text-admin-primary transition hover:bg-admin-primaryLight"
      >
        Xem thêm — tất cả phiên
        <ArrowRight size={14} />
      </button>
      {allOpen ? <SessionsModal onClose={() => setAllOpen(false)} nameBySlug={nameBySlug} /> : null}
    </ChartCard>
  );
}

const fmtDateTime = (iso) => new Date(iso).toLocaleString("vi-VN", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
const SOURCE_LABEL = { Direct: "Trực tiếp", Referrer: "Từ trang khác" };

/** Bảng phiên dùng chung cho thẻ "Phiên gần nhất" và cửa sổ "Tất cả phiên". */
function SessionTable({ rows, nameBySlug, full = false }) {
  if (!rows.length) return <p className="py-6 text-center text-sm text-admin-textMuted">Không có phiên nào</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[640px] text-sm">
        <thead className="text-[11px] uppercase tracking-wider text-admin-textMuted">
          <tr>
            <th className="py-2 pr-3 text-left font-semibold">Bắt đầu</th>
            {full ? <th className="px-3 py-2 text-left font-semibold">Trang vào</th> : null}
            <th className="px-3 py-2 text-left font-semibold">{full ? "Trang cuối" : "Trang hiện tại"}</th>
            <th className="px-3 py-2 text-left font-semibold">Nguồn</th>
            <th className="px-3 py-2 text-left font-semibold">Thiết bị</th>
            <th className="px-3 py-2 text-right font-semibold">Trang</th>
            <th className="py-2 pl-3 text-right font-semibold">Thời lượng</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-admin-border">
          {rows.map((s) => {
            const DIcon = DEVICE_ICON[s.device] || Monitor;
            return (
              <tr key={s.id}>
                <td className="whitespace-nowrap py-2 pr-3 text-xs text-admin-textMuted" title={fmtDateTime(s.startedAt)}>
                  <span className={cx("mr-1.5 inline-block h-2 w-2 rounded-full", s.online ? "bg-emerald-500" : "bg-slate-300")} />
                  {full ? fmtDateTime(s.startedAt) : ago(s.startedAt)}
                </td>
                {full ? (
                  <td className="max-w-[220px] px-3 py-2">
                    <span className="block truncate text-admin-text" title={s.landing}>{pathLabel(s.landing, nameBySlug)}</span>
                  </td>
                ) : null}
                <td className="max-w-[240px] px-3 py-2">
                  <span className="block truncate text-admin-text" title={s.page}>{pathLabel(s.page, nameBySlug)}</span>
                  {s.user ? <span className="block truncate text-[11px] text-admin-textMuted">{s.user}</span> : null}
                </td>
                <td className="px-3 py-2 text-xs text-admin-text">{SOURCE_LABEL[s.source] || s.source}</td>
                <td className="px-3 py-2 text-xs text-admin-text">
                  <span className="inline-flex items-center gap-1.5 whitespace-nowrap"><DIcon size={13} className="text-admin-textMuted" /> {s.browser} · {s.os}</span>
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-admin-text">{fmtNum(s.pageViews)}</td>
                <td className="py-2 pl-3 text-right tabular-nums text-admin-text">{fmtDur(s.duration)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const DEVICE_OPTIONS = [
  { value: "", label: "Mọi thiết bị" },
  { value: "mobile", label: "Điện thoại" },
  { value: "desktop", label: "Máy tính" },
  { value: "tablet", label: "Máy tính bảng" },
];
const EMPTY_FILTERS = { q: "", device: "", source: "", from: "", to: "", loggedIn: false };

/** Cửa sổ "Tất cả phiên": phân trang phía server + lọc thiết bị / nguồn / ngày / trang / người đăng nhập. */
function SessionsModal({ onClose, nameBySlug }) {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [qInput, setQInput] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [state, setState] = useState({ loading: true, error: "", data: null });
  const seq = useRef(0);

  // Gõ tìm kiếm → chờ 300ms mới lọc
  useEffect(() => {
    const t = setTimeout(() => setFilters((f) => (f.q === qInput.trim() ? f : { ...f, q: qInput.trim() })), 300);
    return () => clearTimeout(t);
  }, [qInput]);
  useEffect(() => setPage(1), [filters, limit]);

  useEffect(() => {
    const id = ++seq.current;
    setState((s) => ({ ...s, loading: true, error: "" }));
    const qs = new URLSearchParams({ page: String(page), limit: String(limit) });
    Object.entries(filters).forEach(([k, v]) => {
      if (k === "loggedIn") {
        if (v) qs.set(k, "1");
      } else if (v) qs.set(k, v);
    });
    apiFetch(`${API}/analytics/admin/sessions?${qs.toString()}`)
      .then((data) => id === seq.current && setState({ loading: false, error: "", data }))
      .catch((e) => id === seq.current && setState((s) => ({ ...s, loading: false, error: e?.message || "Không tải được danh sách phiên" })));
  }, [filters, page, limit]);

  const d = state.data;
  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setFilters((f) => ({ ...f, [k]: v }));
  };
  const active = Boolean(filters.q || filters.device || filters.source || filters.from || filters.to || filters.loggedIn);
  const sel = cx(iCls, "!h-9 !w-auto !py-0 !text-sm");

  return (
    <Modal
      open
      onClose={onClose}
      maxWidthClass="max-w-6xl"
      title={
        <div>
          <p className="text-base font-semibold text-admin-text">Tất cả phiên truy cập</p>
          <p className="text-xs text-admin-textMuted">{d ? `${fmtNum(d.total)} phiên${active ? " khớp bộ lọc" : ""} · đã loại bot` : "Đang tải…"}</p>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <SearchBar value={qInput} onChange={(e) => setQInput(e.target.value)} placeholder="Tìm theo trang (vd /map, dinh-3)…" className="w-full sm:w-64" />
          <select value={filters.device} onChange={set("device")} aria-label="Thiết bị" className={sel}>
            {DEVICE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <select value={filters.source} onChange={set("source")} aria-label="Nguồn" className={sel}>
            <option value="">Mọi nguồn</option>
            {(d?.sources || []).map((s) => (
              <option key={s} value={s}>{SOURCE_LABEL[s] || s}</option>
            ))}
          </select>
          <label className="flex items-center gap-1.5 text-xs text-admin-textMuted">
            Từ <input type="date" value={filters.from} onChange={set("from")} className={sel} />
          </label>
          <label className="flex items-center gap-1.5 text-xs text-admin-textMuted">
            đến <input type="date" value={filters.to} onChange={set("to")} className={sel} />
          </label>
          <label className="flex items-center gap-2 text-sm text-admin-text">
            <input type="checkbox" checked={filters.loggedIn} onChange={set("loggedIn")} className="h-4 w-4 accent-[rgb(var(--admin-primary))]" />
            Chỉ người đã đăng nhập
          </label>
          {active ? (
            <button
              type="button"
              onClick={() => {
                setQInput("");
                setFilters(EMPTY_FILTERS);
              }}
              className="text-xs font-semibold text-admin-primary hover:underline"
            >
              Xoá lọc
            </button>
          ) : null}
        </div>

        {state.error ? (
          <p className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{state.error}</p>
        ) : !d ? (
          <div className="space-y-2">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-9 w-full" />
            ))}
          </div>
        ) : (
          <div className={cx("transition-opacity", state.loading && "opacity-60")}>
            <SessionTable rows={d.items} nameBySlug={nameBySlug} full />
          </div>
        )}

        {d && d.total > 0 ? (
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-admin-border">
            <Pagination page={d.page} pages={d.pages} total={d.total} onChange={setPage} />
            <label className="flex items-center gap-2 px-5 py-2 text-xs text-admin-textMuted">
              Hiện
              <select value={limit} onChange={(e) => setLimit(Number(e.target.value))} className={cx(iCls, "!h-8 !w-auto !py-0 !text-xs")} aria-label="Số dòng mỗi trang">
                {[25, 50, 100].map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
              dòng
            </label>
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
